import { useEffect, useState } from "react";
import { Box, useMediaQuery } from "@mui/material"
import { useForm } from "react-hook-form";
import { BackdropComponent, Controls } from "../../../components";
import { useTranslation } from "react-i18next";
import { IInfoComponentProps } from "../../../types/editProfile.types";
import DialogModalHOC from "../components/DialogModalHOC";
import Grid from '@mui/material/Grid2'
import AutoSearch from "../../../components/controls/autoSearch/AutoSearch";
import PracticeTransMsg from "../../onboard/practiceInformation/transMessage";
import { infoFormSchemaResolver, modifyStateList, practiceInfoFormDefaults } from "../../onboard/practiceInformation/practiceInfo.helper";
import { useMutation, useQuery } from "react-query";
import { getPracticeTypeRefURL, getQueryData, getStateRefURL, updatePracticeInfoURL, withCredentials, instance as axiosInstance } from "../../../services";
import { modifyCommonDropDownData } from "../../common.helper";
import { useAppContext, useSnackbar } from "../../../contexts";
import { validateErrorMsg } from "../../../utils/error.utils";
import { theme } from "../../../utils/theme/godzilla_light";

const EditPracticeInfo = (props: IInfoComponentProps) => {
    const isMobile = useMediaQuery(theme.breakpoints.down(950))
    const { isVisible, onCloseDialog, data } = props
    const { t } = useTranslation();
    const { providerId } = useAppContext();
    const { showSnackbar } = useSnackbar();

    const [typeList, setTypeList] = useState<any>([])
    const [stateRefList, setStateRefList] = useState<any[]>([])
    const [practiceID, setPracticeID] = useState("")
    const [formSaveLoader, setFormSaveLoader] = useState(false)

    const {
        handleSubmit,
        control,
        formState: { errors },
        reset: formReset,
        watch
    } = useForm<any>({
        resolver: infoFormSchemaResolver(t),
        defaultValues: practiceInfoFormDefaults
    });
    const practiceType = watch("practice_type");

    const { data: practiceTypeRefData, isLoading: practiceRefLoading } = useQuery("practiceTypeRefData", async () =>
        getQueryData(getPracticeTypeRefURL, withCredentials())
    );

    const { data: stateRefListData, isLoading: stateRefListLoading } = useQuery("stateListRefData", async () =>
        getQueryData(getStateRefURL, withCredentials())
    );
    useEffect(() => {
        if (practiceTypeRefData?.data) {
            const formatedTypes = modifyCommonDropDownData(practiceTypeRefData?.data)
            setTypeList(formatedTypes)
        }
    }, [practiceTypeRefData])

    useEffect(() => {
        if (stateRefListData?.data) {
            const formatedStateListData = modifyStateList(stateRefListData?.data)
            setStateRefList(formatedStateListData)
        }
    }, [stateRefListData])

    useEffect(() => {
        const formPracticeValues = { ...practiceInfoFormDefaults };

        if (data) {
            const prefillPracticeInfoData = data
            formPracticeValues.organization_name = prefillPracticeInfoData?.orgName
            formPracticeValues.practice_type = prefillPracticeInfoData?.type
            formPracticeValues.organization_address_1 = prefillPracticeInfoData?.address?.street1
            formPracticeValues.organization_address_2 = prefillPracticeInfoData?.address?.street2
            formPracticeValues.city = prefillPracticeInfoData?.address?.city
            formPracticeValues.state = prefillPracticeInfoData?.address?.state
            formPracticeValues.zip = prefillPracticeInfoData?.address?.zip
            formPracticeValues.practice_type_specify = prefillPracticeInfoData?.otherTypeDetail
            formReset(formPracticeValues)
            setPracticeID(prefillPracticeInfoData.practiceId)
        }
    }, [data, formReset])

    const submitPracticeMutation = useMutation((params: any) => {
        setFormSaveLoader(true)
        return axiosInstance().put(updatePracticeInfoURL(providerId, practiceID), params, withCredentials())
    },
        {
            onSuccess: () => {
                if(onCloseDialog){
                    onCloseDialog()
                }
            },
            onError: (error: any) => {
                showSnackbar(validateErrorMsg(error?.data?.detail),"error")
            },
            onSettled: () => {
                setFormSaveLoader(false)
            }
        }
    );

    const onSaveProfessionalInfo = (practiceFormValues: any) => {
          // @ts-ignore
        amplitude.logEvent("Clicked Save Practice Info Button")
        const practiceParams = {
            "orgName": practiceFormValues.organization_name,
            "address": {
                "street1": practiceFormValues.organization_address_1,
                "street2": practiceFormValues.organization_address_2,
                "city": practiceFormValues.city,
                "state": practiceFormValues.state,
                "zip": practiceFormValues.zip,
                "type": "SERVICE",
                "latitude": 34.0522,
                "longitude": -118.2437
            },
            "type": practiceFormValues.practice_type,
            "otherTypeDetail": practiceFormValues.practice_type_specify
        }
        submitPracticeMutation.mutate(practiceParams)
    }

    return (
        <DialogModalHOC
            boxTitle={t(PracticeTransMsg.PRACTICE_INFO_TITLE)}
            isOpen={isVisible}
            handleSave={handleSubmit(onSaveProfessionalInfo)}
            onClose={onCloseDialog}>
            <Box component={'form'} 
              sx={{
                mt: "1.5rem",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column"
                }}>
                <Controls.Input
                    name="organization_name"
                    label={t(PracticeTransMsg.ORGANIZATION_LABEL)}
                    defaultValue={''}
                    control={control}
                    required
                    sx={{ mt: '0px' }}
                    error={errors.organization_name || ''}
                />
                <AutoSearch
                    control={control}
                    name='practice_type'
                    label={t(PracticeTransMsg.PRACTICE_TYPE_LABEL)}
                    options={typeList}
                    defaultValue={''}
                    sx={{ mt: '12px' }}
                    error={errors.practice_type || ''}
                />

                {practiceType === "Other" ?
                    <Controls.Input
                        name="practice_type_specify"
                        label={t(PracticeTransMsg.PRACTICE_TYPE_SPECIFY_LABEL)}
                        defaultValue={''}
                        control={control}
                        required
                        error={errors.practice_type_specify || ''}
                    />
                    : null}

                <Controls.Input
                    name="organization_address_1"
                    label={t(PracticeTransMsg.ORGANIZATION_ADDRESS_LINE_1)}
                    defaultValue={''}
                    control={control}
                    required
                    error={errors.organization_address_1 || ''}
                />

                <Controls.Input
                    name="organization_address_2"
                    label={t(PracticeTransMsg.ORGANIZATION_ADDRESS_LINE_2)}
                    defaultValue={''}
                    control={control}
                    error={errors.organization_address_2 || ''}
                />
                <Grid container sx={{ mt: '12px',flexWrap: 'wrap' ,flexDirection: isMobile ? "column":"row"}} spacing={"10px"}>
                    <Grid flex={0.38} maxWidth={"220px"}>
                        <Controls.Input
                            name="city"
                            label={t(PracticeTransMsg.CITY_LABEL)}
                            defaultValue={''}
                            control={control}
                            required
                            sx={{ mt: '0px' }}
                            error={errors.city || ''}
                        />
                    </Grid>
                    <Grid flex={0.24} maxWidth={"120px"}>
                        <AutoSearch
                            control={control}
                            name='state'
                            label={t(PracticeTransMsg.STATE_LABEL)}
                            options={stateRefList}
                            defaultValue={''}
                            error={errors.state || ''}
                            required
                        />
                    </Grid>
                    <Grid flex={0.38} maxWidth={"220px"}>
                        <Controls.Input
                            name="zip"
                            label={t(PracticeTransMsg.ZIP_LABEL)}
                            defaultValue={''}
                            required
                            sx={{ mt: '0px' }}
                            control={control}
                            error={errors.zip || ''}
                        />
                    </Grid>
                </Grid>
                <BackdropComponent showBackdrop={practiceRefLoading || stateRefListLoading || formSaveLoader} />
            </Box>
        </DialogModalHOC>
    )
}

export default EditPracticeInfo